<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'report',
    path: '/charts/:dashboardId',
    component: 'ReportView',
    params: [
      {
        name: 'dashboardId',
        prop: 'dashboardId',
      },
    ],
    icon: 'akar-icons:statistic-up',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ReportView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    dashboardId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const reportUrl = ref(null);
  const reportTitle = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    reportUrl: reportUrl.value,
    reportTitle: reportTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_reportUrl = async () => {
    return $fn.value
      .fetch('get_superset_dashboard_token', {
        parameters: { id: props.dashboardId },
      })
      .then((resp) => resp.url);
  };
  const $$load_reportTitle = async () => {
    return $fn.value
      .fetch('list_superset_dashboards')
      .then(
        (p) =>
          (
            _.find(
              p.result,
              (d) => String(d.id) === String(props.dashboardId)
            ) || {}
          ).dashboard_title
      )
      .catch(() => undefined);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'reportUrl' },
      { name: 'reportTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { reportUrl: $$load_reportUrl, reportTitle: $$load_reportTitle },
    ux: {},
    dataRefs: {
      reportUrl,
      reportTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: reportTitle.value || $fn.value.gettext('Report'),
        icon: 'akar-icons:statistic-up',
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.dashboardId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dashboardId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_yel7i8d8c_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_yel7i8d8c =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_yel7i8d8c_getActionArgs,
    });
  const inst_evh_7315092382398562_close_juiy4b60b =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_yel7i8d8c,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_juiy4b60b],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_yikwdivwb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_arcp1neeo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_yikwdivwb,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_arcp1neeo],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_juiy4b60b,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_arcp1neeo,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_juiy4b60b = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_juiy4b60b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_arcp1neeo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_arcp1neeo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ReportView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpKeepAliveIframe
      kind="url"
      :cacheKey="dashboardId"
      :src="reportUrl"
      class="w-100 h-100"
      v-if="reportUrl"
    >
    </ItpKeepAliveIframe>
  </ItpPage>
</template>
